import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AdminInfo } from '../types';
import { API_URL } from '../config';
import './AdminInfoPage.css';

const AdminInfoPage: React.FC = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<AdminInfo[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<AdminInfo[]>([]);
  const [sections, setSections] = useState<AdminInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState<AdminInfo | null>(null);
  const [linkedSection, setLinkedSection] = useState<AdminInfo | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const resourcesRes = await axios.get(`${API_URL}/api/admininfo`, config);
      
      const adminInfos = resourcesRes.data.adminInfos || [];
      setDocuments(adminInfos.filter((r: AdminInfo) => r.type === 'document'));
      setEmergencyContacts(adminInfos.filter((r: AdminInfo) => r.type === 'emergency-contact'));
      const sortedSections = adminInfos
        .filter((r: AdminInfo) => r.type === 'section')
        .sort((a: AdminInfo, b: AdminInfo) => {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return aDate - bDate;
        });
      setSections(sortedSections);
    } catch (error) {
      console.error('Failed to fetch admin info:', error);
      setDocuments([]);
      setEmergencyContacts([]);
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDeleteResource = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admininfo/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAll();
    } catch (error) {
      console.error('Failed to delete admin info:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Only admin can view this page
  if (user?.role !== 'admin') {
    return <div className="loading">Access denied. Admin only.</div>;
  }

  const formatDateOnlyUTC = (raw: string) => {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { timeZone: 'UTC' });
  };

  const formatDateLocal = (raw: string) => {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  };

  const getDocumentDateMeta = (doc: AdminInfo) => {
    if (doc.date) {
      const v = formatDateOnlyUTC(doc.date);
      return v ? { label: 'Date', value: v } : null;
    }
    const fallback = doc.updatedAt || doc.createdAt;
    if (!fallback) return null;
    const v = formatDateLocal(fallback);
    return v ? { label: 'Updated', value: v } : null;
  };

  const sectionKeys = new Set(
    sections.flatMap((section) => [section._id, section.title])
  );
  const generalDocuments = documents.filter(
    (doc) => !doc.category || !sectionKeys.has(doc.category)
  );

  return (
    <div className="admin-info-container">
      <div className="admin-info-header">
        <h1>Admin Information</h1>
        <p>Private admin-only documents, contacts, and information</p>
      </div>

      <section className="admin-info-section">
        <div className="section-header">
          <h2>Important Contacts</h2>
          <button className="add-btn" onClick={() => {
            setEditingResource({ type: 'emergency-contact' } as AdminInfo);
            setShowResourceModal(true);
          }}>
            + Add Contact
          </button>
        </div>
        <div className="emergency-grid">
          {emergencyContacts.map((contact) => (
            <div key={contact._id} className="emergency-card">
              <h3>{contact.title}</h3>
              <p className="emergency-phone">{contact.phone}</p>
              <p className="emergency-description">{contact.description}</p>
              <div className="card-actions">
                <button onClick={() => {
                  setEditingResource(contact);
                  setShowResourceModal(true);
                }}>Edit</button>
                <button onClick={() => handleDeleteResource(contact._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-info-section">
        <div className="section-header">
          <h2>Documents</h2>
          <button className="add-btn" onClick={() => {
            setLinkedSection(null);
            setEditingResource({ type: 'document' } as AdminInfo);
            setShowResourceModal(true);
          }}>
            + Add Document
          </button>
        </div>
        <div className="documents-list">
          {generalDocuments.map((doc) => (
            <div key={doc._id} className="document-card">
              <div className="document-header">
                <h3>{doc.title}</h3>
                {doc.category && <span className={`doc-category ${doc.category}`}>{doc.category}</span>}
              </div>
              <p className="document-description">{doc.description?.replace(/^[•\-*]\s*/gm, '')}</p>
              <div className="document-footer">
                {getDocumentDateMeta(doc) && (
                  <span className="document-date">
                    {getDocumentDateMeta(doc)!.label}: {getDocumentDateMeta(doc)!.value}
                  </span>
                )}
                {(() => {
                  const attachments =
                    doc.attachments && doc.attachments.length > 0
                      ? doc.attachments
                      : doc.attachment
                      ? [doc.attachment]
                      : [];
                  const safe = attachments.filter(
                    (a) => !!a && (!!a.filename || !!(a as any)._id)
                  );
                  if (safe.length === 0) return null;
                  return (
                    <div className="document-actions">
                      {safe.map((a) => (
                        <a
                          key={a.filename || (a as any)._id}
                          href={`${API_URL}${a.url}`}
                          download={a.originalName || a.filename}
                          className="download-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download: {a.originalName || a.filename || 'Attachment'}
                        </a>
                      ))}
                    </div>
                  );
                })()}
                <div className="card-actions">
                  <button onClick={() => {
                    setLinkedSection(null);
                    setEditingResource(doc);
                    setShowResourceModal(true);
                  }}>Edit</button>
                  <button onClick={() => handleDeleteResource(doc._id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {sections.map((section) => {
        const sectionDocs = documents.filter(
          (doc) => doc.category === section._id || doc.category === section.title
        );

        return (
          <section key={section._id} className="admin-info-section">
            <div className="section-header">
              <h2>{section.title}</h2>
              <div className="section-actions">
                <button
                  onClick={() => {
                    setLinkedSection(section);
                    setEditingResource({
                      type: 'document',
                      category: section._id,
                    } as AdminInfo);
                    setShowResourceModal(true);
                  }}
                >
                  Add Document
                </button>
                <button onClick={() => {
                  setLinkedSection(null);
                  setEditingResource(section);
                  setShowResourceModal(true);
                }}>Edit</button>
                <button onClick={() => handleDeleteResource(section._id)}>Delete</button>
              </div>
            </div>
            <div className="section-content">
              {section.description && (
                <p className="section-description">{section.description}</p>
              )}
              {section.content && (
                <div className="section-body">{section.content}</div>
              )}
            </div>

            {sectionDocs.length > 0 && (
              <div className="documents-list section-documents">
                {sectionDocs.map((doc) => (
                  <div key={doc._id} className="document-card">
                    <div className="document-header">
                      <h3>{doc.title}</h3>
                      {doc.category && (
                        <span className="doc-category section-document">
                          Section Document
                        </span>
                      )}
                    </div>
                    <p className="document-description">{doc.description?.replace(/^[•\-*]\s*/gm, '')}</p>
                    <div className="document-footer">
                      {getDocumentDateMeta(doc) && (
                        <span className="document-date">
                          {getDocumentDateMeta(doc)!.label}: {getDocumentDateMeta(doc)!.value}
                        </span>
                      )}
                      <div className="document-actions">
                        {(() => {
                          const attachments =
                            doc.attachments && doc.attachments.length > 0
                              ? doc.attachments
                              : doc.attachment
                              ? [doc.attachment]
                              : [];
                          const safe = attachments.filter(
                            (a) => !!a && (!!a.filename || !!(a as any)._id)
                          );
                          if (safe.length === 0) return null;
                          return (
                            <div className="document-actions">
                              {safe.map((a) => (
                                <a
                                  key={a.filename || (a as any)._id}
                                  href={`${API_URL}${a.url}`}
                                  download={a.originalName || a.filename}
                                  className="download-btn"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download: {a.originalName || a.filename || 'Attachment'}
                                </a>
                              ))}
                            </div>
                          );
                        })()}
                        <div className="card-actions">
                          <button onClick={() => {
                            setLinkedSection(section);
                            setEditingResource(doc);
                            setShowResourceModal(true);
                          }}>Edit</button>
                          <button onClick={() => handleDeleteResource(doc._id)}>Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <div className="admin-add-section">
        <button className="add-section-btn" onClick={() => {
          setLinkedSection(null);
          setEditingResource({ type: 'section' } as AdminInfo);
          setShowResourceModal(true);
        }}>
          + Add New Section
        </button>
      </div>

      {showResourceModal && (
        <AdminInfoModal
          resource={editingResource}
          parentSection={linkedSection}
          onClose={() => {
            setShowResourceModal(false);
            setEditingResource(null);
            setLinkedSection(null);
          }}
          onSuccess={() => {
            setShowResourceModal(false);
            setEditingResource(null);
            setLinkedSection(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};

// Admin Info Modal Component
const AdminInfoModal: React.FC<{
  resource: AdminInfo | null;
  parentSection?: AdminInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ resource, parentSection, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: resource?.title || '',
    type: resource?.type || 'document',
    category: resource?.category || parentSection?._id || '',
    description: resource?.description || '',
    content: resource?.content || '',
    phone: resource?.phone || '',
    date: resource?.date ? new Date(resource.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
  });
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<
    NonNullable<AdminInfo['attachments']>
  >(() => {
    if (resource?.attachments && resource.attachments.length > 0) return resource.attachments;
    if (resource?.attachment) return [resource.attachment];
    return [];
  });

  const typeSelectDisabled = !!parentSection || resource?.type === 'section' || resource?.type === 'emergency-contact';
  const typeOptions = resource?.type === 'section'
    ? [{ value: 'section', label: 'Section' }]
    : resource?.type === 'emergency-contact'
    ? [{ value: 'emergency-contact', label: 'Contact' }]
    : [
        { value: 'document', label: 'Document' },
        { value: 'emergency-contact', label: 'Contact' },
      ];

  useEffect(() => {
    if (parentSection) {
      setFormData((prev) => ({
        ...prev,
        type: 'document',
        category: parentSection._id,
      }));
    }
  }, [parentSection]);

  const handleRemoveExistingAttachment = async (identifier: string) => {
    if (!resource?._id) return;
    if (!window.confirm('Remove this attachment?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admininfo/${resource._id}/attachments/${encodeURIComponent(identifier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentAttachments((prev) =>
        prev.filter((a) => (a.filename || a._id) !== identifier)
      );
      alert('Attachment removed successfully');
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      alert('Failed to remove attachment');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('type', formData.type);
      const categoryToSend = parentSection?._id || formData.category;
      formDataToSend.append('category', categoryToSend);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('date', formData.date);
      
      newAttachments.forEach((f) => {
        formDataToSend.append('files', f);
      });

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      };

      if (resource?._id) {
        await axios.put(`${API_URL}/api/admininfo/${resource._id}`, formDataToSend, config);
      } else {
        await axios.post(`${API_URL}/api/admininfo`, formDataToSend, config);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save admin info:', error);
      alert('Failed to save admin info. Please try again.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{resource?._id ? 'Edit Admin Info' : 'Add New Admin Info'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="modal-form-content">
            <div className="form-group">
            <label>Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              required
              disabled={typeSelectDisabled}
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          {formData.type === 'document' && (
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                value={parentSection ? parentSection.title : formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., bylaw, policy, form"
                disabled={!!parentSection}
              />
            </div>
          )}
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
            />
          </div>
          {formData.type === 'section' && (
            <div className="form-group">
              <label>Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={5}
              />
            </div>
          )}
          {formData.type === 'emergency-contact' && (
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
          )}
          {formData.type === 'document' && (
            <>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>File</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    setNewAttachments((prev) => [...prev, ...files]);
                    e.currentTarget.value = '';
                  }}
                />
                {resource?._id && currentAttachments.length > 0 && (
                  <div className="current-attachment">
                    <p className="current-file">Current attachments:</p>
                    <ul>
                      {currentAttachments
                        .filter((a) => !!a && (!!a.filename || !!a._id))
                        .map((a) => (
                        <li key={a.filename || a._id}>
                          <a
                            href={`${API_URL}${a.url}`}
                            download={a.originalName || a.filename}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {a.originalName || a.filename || 'Attachment'}
                          </a>
                          <button
                            type="button"
                            className="remove-attachment-btn"
                            onClick={() =>
                              handleRemoveExistingAttachment(a.filename || a._id || '')
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {newAttachments.length > 0 && (
                  <div className="current-attachment">
                    <p className="new-file">New files to upload:</p>
                    <ul>
                      {newAttachments.map((f, idx) => (
                        <li key={`${f.name}-${f.size}-${idx}`}>
                          <span>{f.name}</span>
                          <button
                            type="button"
                            className="remove-attachment-btn"
                            onClick={() =>
                              setNewAttachments((prev) => prev.filter((_, i) => i !== idx))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {resource?._id ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminInfoPage;
